// src/components/gavion/integrations/api/fakeM365Api.ts
// Demo/test M365Api over a RecordStore (dialer/api/recordStore). Mirrors the edge contracts' codes.
import type { RecordStore } from "../../dialer/api/recordStore";
import { fakeTouchDecided } from "../../ai/api/fakeFollowUp";
import {
  M365ApiError,
  type M365Api,
  type MicrosoftFeature,
  type MicrosoftStatus,
  type TeamsConfig,
} from "./types";

const FEATURES: [MicrosoftFeature, string][] = [
  ["mail_sync", "mail.readwrite"],
  ["send", "mail.send"],
  ["calendar", "calendars.readwrite"],
  ["teams_meetings", "onlinemeetings.readwrite"],
  ["archive", "mail.read.shared"],
];
type Row = Record<string, unknown> & { id: number };

export function createFakeM365Api(o: {
  store: RecordStore;
  me: number;
  isAdmin: boolean;
  now?: () => Date;
  translate?: (key: string) => string;
}): M365Api {
  const translate = o.translate ?? ((k: string) => k);
  const now = o.now ?? (() => new Date());
  const account = async () =>
    (await o.store.list<Row>("integration_accounts")).find(
      (a) => a.sales_id === o.me && a.provider === "microsoft",
    ) ?? null;
  const config = async () =>
    (await o.store.get<Row & { config: Record<string, unknown> }>(
      "configuration",
      1,
    )) ?? { id: 1, config: {} };
  const teams = async (): Promise<TeamsConfig> => {
    const t =
      (
        (await config()).config as {
          gavion?: { teams?: Record<string, unknown> };
        }
      ).gavion?.teams ?? {};
    return {
      teamId: (t.team_id as string) ?? null,
      teamName: (t.team_name as string) ?? null,
      channelId: (t.channel_id as string) ?? null,
      channelName: (t.channel_name as string) ?? null,
      posterSalesId: (t.poster_sales_id as number) ?? null,
      webhookConfigured: t.webhook_configured === true,
    };
  };
  const writeTeams = async (patch: Record<string, unknown>) => {
    if (!o.isAdmin) throw new M365ApiError("forbidden");
    const c = await config();
    const gavion = (c.config.gavion ?? {}) as Record<string, unknown>;
    await o.store.update("configuration", 1, {
      config: {
        ...c.config,
        gavion: { ...gavion, teams: { ...(gavion.teams as object), ...patch } },
      },
    });
    return teams();
  };
  const demoPatch = (dealId: number, startAt: string | null) => ({
    demo_at: startAt,
    outlook_event_id: startAt ? `demo-event-${dealId}` : null,
    teams_join_url: startAt
      ? `https://teams.microsoft.com/l/meetup-join/demo-${dealId}`
      : null,
  });
  const activeDeal = async (dealId: number) => {
    const d = await o.store.get<Row>("deals", dealId);
    if (!d) throw new M365ApiError("deal_not_found");
    if (!d.demo_at) throw new M365ApiError("no_active_demo");
  };
  return {
    async microsoftStatus() {
      const a = await account();
      const scopes = (a?.scopes as string[] | undefined) ?? [];
      const have = new Set(scopes.map((s) => s.toLowerCase()));
      return {
        status: (a?.status as MicrosoftStatus["status"]) ?? "none",
        scopes,
        missing: FEATURES.filter(
          ([f, s]) => !have.has(s) && (o.isAdmin || f !== "archive"), // GAVION full-email: admins only
        ).map(([f]) => f),
        externalEmail: (a?.external_email as string) ?? null,
        lastSyncAt: (a?.last_sync_at as string) ?? null,
        lastError: (a?.last_error as string) ?? null,
        adminConsentUrl: null,
      };
    },
    async startMicrosoftConnect() {
      return { url: "/parametres/integrations?microsoft=connected" };
    },
    async storeMicrosoftTokens() {
      return this.microsoftStatus();
    },
    async disconnectMicrosoft() {
      const a = await account();
      if (a)
        await o.store.update("integration_accounts", a.id, {
          status: "disconnected",
        });
    },
    teamsConfig: teams,
    async listTeams() {
      return [{ id: "demo-team", name: "Gavion" }];
    },
    async listChannels() {
      return [{ id: "demo-ventes", name: "Ventes" }];
    },
    saveTeamsChannel: (i) =>
      writeTeams({
        team_id: i.teamId,
        team_name: i.teamName,
        channel_id: i.channelId,
        channel_name: i.channelName,
        poster_sales_id: o.me,
      }),
    setTeamsWebhook: (url) => writeTeams({ webhook_configured: url !== null }),
    async getSignature() {
      const me = await o.store.get<
        Row & { prefs?: { email_signature_html?: string } }
      >("sales", o.me);
      return me?.prefs?.email_signature_html ?? "";
    },
    async saveSignature(html) {
      const me = await o.store.get<Row & { prefs?: Record<string, unknown> }>(
        "sales",
        o.me,
      );
      if (me)
        await o.store.update("sales", o.me, {
          prefs: { ...me.prefs, email_signature_html: html },
        });
    },
    async sendEmail(i) {
      // GAVION ai-email: twin of gavion-graph-send's ai_draft_id checks. A proposed draft must be the caller's (admins:
      // any) and still pending, unless this is a retry of its own send; an on-demand row never blocks (ruling P11).
      // `!= null`, not truthiness: the first demo draft has id 0
      const hasDraft = i.aiDraftId != null;
      const draft = hasDraft
        ? await o.store.get<Row>("ai_email_drafts", i.aiDraftId as number)
        : null;
      if (hasDraft && !draft) throw new M365ApiError("ai_draft_not_found");
      if (draft) {
        if (Number(draft.sales_id) !== o.me && !o.isAdmin)
          throw new M365ApiError("forbidden");
        const sentAs =
          draft.email_message_id == null
            ? null
            : await o.store.get<Row>(
                "email_messages",
                Number(draft.email_message_id),
              );
        const retryOfSent =
          draft.status === "sent" && sentAs?.uid === i.idempotencyKey;
        if (
          draft.trigger !== "on_demand" &&
          draft.status !== "pending" &&
          !retryOfSent
        )
          throw new M365ApiError("ai_draft_not_pending");
      }
      const blocked = (await o.store.list<Row>("suppressions"))
        .filter(
          (s) =>
            s.channel === "email" &&
            [...i.to, ...i.cc].includes(String(s.value)),
        )
        .map((s) => String(s.value));
      if (blocked.length > 0)
        throw new M365ApiError("suppressed", { suppressed: blocked });
      const existing = (await o.store.list<Row>("email_messages")).find(
        (m) => m.uid === i.idempotencyKey,
      );
      if (existing) return { messageId: existing.id, duplicate: true };
      const row = await o.store.create<Row>("email_messages", {
        uid: i.idempotencyKey,
        provider: "graph",
        direction: "outbound",
        origin: "crm",
        status: "sent",
        sales_id: o.me,
        company_id: i.companyId ?? null,
        contact_id: i.contactId ?? null,
        deal_id: i.dealId ?? null,
        template_id: i.templateId ?? null,
        ai_draft_id: null,
        to_emails: i.to,
        cc_emails: i.cc,
        subject: i.subject,
        snippet: i.html
          .replace(/<[^>]*>/g, " ")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 280),
        sent_at: now().toISOString(),
        is_reply: false,
      });
      // twin of ai_draft_mark_sent: same lead, and the same rep or an admin sender (final review I1: an admin's send
      // of another rep's draft marks it sent, so it cannot go out twice); pending (or an expired on-demand row); else
      // no link, no error
      const linkable =
        draft !== null &&
        (Number(draft.sales_id) === o.me || o.isAdmin) &&
        String(draft.company_id ?? "") === String(i.companyId ?? "") &&
        (draft.status === "pending" ||
          (draft.trigger === "on_demand" && draft.status === "expired"));
      if (draft && linkable) {
        await o.store.update("ai_email_drafts", draft.id, {
          status: "sent",
          edited: i.aiDraftEdited ?? false,
          decided_at: now().toISOString(),
          decided_by: o.me,
          email_message_id: row.id,
        });
        await o.store.update("email_messages", row.id, {
          ai_draft_id: draft.id,
        });
        await fakeTouchDecided(o.store, draft.id, "sent"); // GAVION ai follow-up
      }
      return { messageId: row.id };
    },
    async messageBody() {
      return {
        contentType: "html",
        content: `<p>${translate("gavion.compose.message.demo_body")}</p>`,
        attachments: [],
      };
    },
    async attachmentUrl() {
      throw new M365ApiError("message_gone");
    },
    // GAVION full-email: the demo keeps no stored copy (the viewer falls back to the demo body)
    async storedMessage() {
      return null;
    },
    async storedAttachmentUrl() {
      throw new M365ApiError("message_gone");
    },
    async bookDemo(i) {
      await o.store.update(
        "deals",
        i.dealId,
        demoPatch(i.dealId, new Date(i.startAt).toISOString()),
      );
      return {
        status: "created",
        eventId: `demo-event-${i.dealId}`,
        joinUrl: `https://teams.microsoft.com/l/meetup-join/demo-${i.dealId}`,
      };
    },
    async rescheduleDemo(i) {
      await activeDeal(i.dealId);
      await o.store.update("deals", i.dealId, {
        demo_at: new Date(i.startAt).toISOString(),
      });
      return {
        status: "moved",
        eventId: `demo-event-${i.dealId}`,
        joinUrl: `https://teams.microsoft.com/l/meetup-join/demo-${i.dealId}`,
      };
    },
    async cancelDemo(i) {
      await activeDeal(i.dealId);
      await o.store.update("deals", i.dealId, demoPatch(i.dealId, null));
    },
  };
}
